import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { NO_GROUP_TOKEN } from '@ditto/shared';

import {
  useSharedKnowledgeGroupsService,
  useUpdateSharedKnowledgeFileService,
} from '@/services/sharedFiles';
import { ChangeEvent, useEffect, useState, type FC } from 'react';
import { toast } from 'sonner';

export const EditFileModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileId: string;
  initialGroupId: string | null | undefined;
  initialName: string;
}> = ({ open, onOpenChange, fileId, initialGroupId, initialName }) => {
  const { updateFile } = useUpdateSharedKnowledgeFileService();
  const { groups } = useSharedKnowledgeGroupsService();

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(
    initialGroupId ?? null
  );
  const [name, setName] = useState<string>('');

  useEffect(() => {
    setSelectedGroupId(initialGroupId ?? null);
    setName(initialName);
  }, [initialGroupId, initialName, open]);

  const handleGroupChange = (value: string) => {
    setSelectedGroupId(value === NO_GROUP_TOKEN ? null : value);
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleSubmit = () => {
    const { success, errorMessage } = validateName();
    if (!success) {
      toast.error(errorMessage);
      return;
    }

    updateFile({
      fileId,
      patch: {
        groupId: selectedGroupId,
        name,
      },
    });

    onOpenChange(false);
  };

  const validateName = () => {
    if (!name.trim()) {
      return { success: false, errorMessage: 'Name is required' };
    }
    return { success: true, errorMessage: '' };
  };

  const getDisplayValue = () => {
    if (selectedGroupId === null) {
      return 'No Group';
    }
    const group = groups.find(grp => grp.id === selectedGroupId);
    return group?.name || 'Select a group';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit File</DialogTitle>
        </DialogHeader>
        <FlexCol gap="2">
          <Label>Name</Label>
          <Input value={name} onChange={handleNameChange} />
        </FlexCol>
        <FlexCol gap="2">
          <Label>Knowledge Group</Label>
          <Select
            value={selectedGroupId ?? NO_GROUP_TOKEN}
            onValueChange={handleGroupChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a knowledge group">
                {getDisplayValue()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_GROUP_TOKEN}>No Group</SelectItem>
              {groups.map(group => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FlexCol>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
